/**
 * Client adapter over the shared itinerary expansion engine
 * (`@elara/domain/models/itinerary-expansion`).
 *
 * The expansion/grouping/sorting algorithm itself is canonical and lives in the
 * domain package so the web client, the `ListItineraryEvents` flow, and the
 * day-summaries prompt all produce identical events and day structure. This file
 * keeps the client-only concerns: normalizing reactive model instances (with
 * their lazily-hydrated flight option / hotel / place) into the engine's plain
 * `ExpandableItem` shape, plus presentation helpers (icons, time formatting) and
 * the event→item resolver the views use for hydration.
 */

import type {
  DestinationGroup,
  ExpandableItem,
  HotelStayInfo,
  PlannedDestination,
} from '@elara/domain/models/itinerary-expansion';
import type { FlightOption } from '@elara/domain/models/schemas/flight-schemas';
import type {
  ItineraryItemEvent,
  ItineraryItemEventType,
} from '@elara/domain/models/schemas/itinerary-item-event-schemas';
import type { ItineraryItemData } from '@elara/domain/models/schemas/itinerary-item-schemas';

import type { ItineraryItem } from '#models/ItineraryItemModel.js';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { addMinutes, isValid, parseISO } from 'date-fns';

import {
  computeHotelStaysByDay as computeHotelStaysByDayEngine,
  createGroupedHotelEvents as createGroupedHotelEventsEngine,
  expandItineraryItem,
  groupDaysByDestination as groupDaysByDestinationEngine,
} from '@elara/domain/models/itinerary-expansion';
import { placeTypeServesMeal, resolvePlaceEventType } from '@elara/domain/models/place-category';
import { getPlaceIcon } from './place-category';

// Re-export the canonical, environment-agnostic helpers so existing client
// imports keep working against the single source of truth.
export type {
  DestinationDescriptor,
  DestinationGroup,
  HotelStayInfo,
  PlannedDestination,
} from '@elara/domain/models/itinerary-expansion';
export {
  calculateLayoverDuration,
  compareDateTimes,
  compareEvents,
  generateEventId,
  isMultiDay,
  isTravelDay,
} from '@elara/domain/models/itinerary-expansion';

/**
 * Event types that represent structural itinerary state (travel, hotel transitions)
 * rather than planned activities. Days containing only structural events still need
 * activity/dining suggestions.
 */
export const ITINERARY_STRUCTURAL_EVENT_TYPES = new Set<ItineraryItemEventType>([
  'flight_departure',
  'flight_arrival',
  'layover',
  'hotel_checkin',
  'hotel_checkout',
  'destination_arrival',
]);

/**
 * Coarse fallback for {@link isMealEvent}: the two categories that mostly contain places you eat or
 * drink at. Only consulted for an event carrying no `placePrimaryType` — one expanded before that was
 * denormalized, or a place Google gave no type for.
 */
export const ITINERARY_MEAL_EVENT_TYPES = new Set<ItineraryItemEventType>(['place_dining', 'place_nightlife']);

/**
 * Whether this event means the traveler already has food or drink out that day — so the itinerary
 * shouldn't also be suggesting somewhere to eat, which read as though we hadn't noticed the booking.
 *
 * Judged on the place's OWN type (`placePrimaryType`, denormalized onto the event by the expansion
 * engine) rather than its broad category, because the categories cut across the distinction that
 * matters: `pub` and `wine_bar` sit under nightlife alongside `night_club` and `casino`. A pub counts;
 * a nightclub leaves dinner still to sort out.
 */
export function isMealEvent(event: ItineraryItemEvent): boolean {
  const placePrimaryType = (event.metadata as { placePrimaryType?: unknown } | undefined)?.placePrimaryType;
  if (typeof placePrimaryType === 'string' && placePrimaryType.trim() !== '') {
    return placeTypeServesMeal(placePrimaryType);
  }
  return ITINERARY_MEAL_EVENT_TYPES.has(event.type);
}

/** Precomputed placeholder visibility flags for a single itinerary day. */
export interface DayPlaceholders {
  showActivitiesPlaceholder: boolean;
  showHotelPlaceholder: boolean;
  showDiningPlaceholder: boolean;
}

/** Precomputed placeholder data for one destination group and all its days. */
export interface GroupPlaceholders {
  /** Whether to show the "Choose your transportation" section above this group's days. */
  showTransportationSection: boolean;
  /** Per-day placeholder flags keyed by YYYY-MM-DD date key. */
  dayPlaceholders: Record<string, DayPlaceholders>;
}

/**
 * Computes placeholder visibility flags for every group and day in the itinerary.
 * Extracted here so web and mobile consumers share identical logic — views just read
 * the precomputed flags instead of each reimplementing the rules.
 *
 * Rules encoded:
 * - Transportation section: shown when there's no flight getting the traveler *to* this group's
 *   destination. For non-first groups that means the previous group's last day has no
 *   `flight_departure` (no flight connecting the two destinations). For the FIRST group it means the
 *   group's own first day carries no inbound travel (no `flight_departure` / `flight_arrival` /
 *   `destination_arrival`) — so a single-destination trip with no flight booked still prompts
 *   "Find flights" instead of silently omitting it (ELARA-973).
 * - Hotel placeholder: not last day of group, no hotel booked tonight, not a departure day.
 * - Dining placeholder: not a departure day, nothing already booked to eat or drink at
 *   (see {@link isMealEvent} — a pub counts, not just a restaurant).
 * - Activities placeholder: not a departure day, no non-structural events planned for the day.
 *
 * The last day of the trip is deliberately NOT excluded: a final day with no flight home booked yet
 * is still a day to fill, and suppressing its suggestions left the itinerary ending on a blank day.
 * A day the traveler is flying out of is still excluded — that day is already spoken for — which
 * covers the common last day that does carry a departure.
 */
export function computeGroupPlaceholders(
  groups: DestinationGroup[],
  eventsByDay: Record<string, ItineraryItemEvent[]>,
  hotelStaysByDay: Record<string, HotelStayInfo[]>,
): GroupPlaceholders[] {
  return groups.map((group, groupIndex): GroupPlaceholders => {
    const prevGroup = groupIndex > 0 ? groups[groupIndex - 1] : undefined;
    const prevGroupLastDayKey = prevGroup?.dateKeys.at(-1);
    const prevLastDayEvents = prevGroupLastDayKey ? (eventsByDay[prevGroupLastDayKey] ?? []) : [];

    // The first day of this group — its inbound-travel signal for the first-group case.
    const firstDayKey = group.dateKeys[0];
    const firstDayEvents = firstDayKey ? (eventsByDay[firstDayKey] ?? []) : [];
    const firstDayHasInboundTravel = firstDayEvents.some(
      (e) => e.type === 'flight_departure' || e.type === 'flight_arrival' || e.type === 'destination_arrival',
    );

    const showTransportationSection =
      groupIndex > 0
        ? !prevLastDayEvents.some((e) => e.type === 'flight_departure')
        : !firstDayHasInboundTravel;

    const dayPlaceholders: Record<string, DayPlaceholders> = {};
    const groupDateKeys = group.dateKeys;

    // Only show the hotel placeholder on the first day in the group that lacks accommodation —
    // showing it on every unbooked night is redundant noise.
    const firstUnaccommodatedIndex = groupDateKeys.findIndex((dateKey, i) => {
      if (i === groupDateKeys.length - 1) return false;
      const hotelStays = hotelStaysByDay[dateKey] ?? [];
      const hasHotelStayTonight = hotelStays.some((stay) => !stay.isCheckoutDay);
      const hasFlightDeparture = (eventsByDay[dateKey] ?? []).some((e) => e.type === 'flight_departure');
      return !hasHotelStayTonight && !hasFlightDeparture;
    });

    for (let dayIndex = 0; dayIndex < groupDateKeys.length; dayIndex++) {
      const dateKey = groupDateKeys[dayIndex]!;
      const events = eventsByDay[dateKey] ?? [];

      const hasFlightDeparture = events.some((e) => e.type === 'flight_departure');
      const hasDining = events.some((e) => isMealEvent(e));

      dayPlaceholders[dateKey] = {
        showActivitiesPlaceholder:
          !hasFlightDeparture && !events.some((e) => !ITINERARY_STRUCTURAL_EVENT_TYPES.has(e.type)),
        showHotelPlaceholder: dayIndex === firstUnaccommodatedIndex,
        showDiningPlaceholder: !hasFlightDeparture && !hasDining,
      };
    }

    return { showTransportationSection, dayPlaceholders };
  });
}

/**
 * Local calendar-day key (YYYY-MM-DD) in the viewer's timezone. Past-event collapsing keys off the
 * *local* day so "today" matches the calendar day the traveler is actually living, independent of how
 * the stored datetimes happen to be zoned.
 */
export function toLocalDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * The decision the itinerary views need to collapse already-happened days behind a "See past events"
 * toggle (ELARA-740). Shared so web and mobile behave identically.
 */
export interface PastEventsCollapse {
  /** Whether the toggle should appear at all — true only when the trip is active AND has past days. */
  canCollapse: boolean;
  /** The local calendar-day key treated as "today". */
  todayKey: string;
  /**
   * Whether a given day should be hidden while collapsed. Returns `false` for every day when the trip
   * isn't collapsible, so callers can apply it unconditionally without re-checking `canCollapse`.
   */
  isPastDay: (dayKey: string) => boolean;
}

/**
 * Decides how the "past events" collapse should behave for a set of ordered itinerary day keys.
 *
 * Rationale: while a traveler is *on* a trip, days that have already happened are noise — the current
 * day should be the focus. So past days collapse behind a "See past events" toggle, but ONLY while the
 * trip is active (today falls within the itinerary's day range) and at least one day precedes today.
 * Trips that haven't started and trips that have finished show everything — there's no "current day"
 * to keep in focus.
 *
 * @param orderedDayKeys - every itinerary day's local key (YYYY-MM-DD), in chronological order.
 * @param now - the current instant; injectable so the behaviour is deterministic in tests.
 */
export function computePastEventsCollapse(orderedDayKeys: string[], now: Date = new Date()): PastEventsCollapse {
  const todayKey = toLocalDateKey(now);
  const firstKey = orderedDayKeys[0];
  const lastKey = orderedDayKeys.at(-1);
  const isActive = firstKey !== undefined && lastKey !== undefined && firstKey <= todayKey && todayKey <= lastKey;
  const hasPastDay = orderedDayKeys.some((key) => key < todayKey);
  const canCollapse = isActive && hasPastDay;
  return {
    canCollapse,
    todayKey,
    isPastDay: (dayKey: string) => canCollapse && dayKey < todayKey,
  };
}

/**
 * Item shape the client adapter accepts: a full reactive model instance (with
 * `flightOption` / `roomOption` / `hotel` / `place` getters) or plain data (e.g.
 * in tests) carrying `referenceData`.
 */
export type ExpandableItineraryItem = ItineraryItem | (ItineraryItemData & { id: string });

/** Resolves hotel ID for a room item (model or plain data). */
function getHotelIdForRoomItem(item: ExpandableItineraryItem): string {
  if ('roomOption' in item && item.type === 'room' && item.roomOption?.hotelId) {
    return item.roomOption.hotelId;
  }
  const ref = item.referenceData as { hotelId?: string } | undefined;
  return ref?.hotelId ?? item.id;
}

/** Resolves hotel display name for a room item (model or plain data). */
function getHotelNameForRoomItem(item: ExpandableItineraryItem): string {
  if ('hotel' in item && item.type === 'room' && item.hotel?.name) {
    return item.hotel.name;
  }
  const ref = item.referenceData as { hotelName?: string } | undefined;
  return ref?.hotelName ?? 'Hotel';
}

/**
 * Resolves the room item's destination from its hotel's pre-resolved `destination`
 * reference (best join key) plus address fallbacks. Only available on hydrated model
 * instances; plain-data test items return no fields.
 */
function getHotelDestinationFields(
  item: ExpandableItineraryItem,
): Pick<ExpandableItem, 'hotelDestinationId' | 'hotelDestinationName' | 'hotelCountryCode' | 'hotelCity'> {
  if (!('hotel' in item) || item.type !== 'room') return {};
  const hotel = item.hotel;
  if (!hotel) return {};
  const destination = hotel.destination;
  const city = hotel.address?.city ?? undefined;
  const countryCode = destination?.countryCode ?? hotel.address?.countryCode ?? undefined;
  return {
    ...(destination?.id ? { hotelDestinationId: destination.id } : {}),
    ...(destination?.name ? { hotelDestinationName: destination.name } : {}),
    ...(countryCode ? { hotelCountryCode: countryCode } : {}),
    ...(city ? { hotelCity: city } : {}),
  };
}

/**
 * Resolves the per-item cost via the model's reactive `cost` getter (flight price,
 * room rate total, else undefined). Plain-data test items without the getter resolve
 * to undefined.
 */
function getResolvedCost(item: ExpandableItineraryItem): ExpandableItem['cost'] {
  return 'cost' in item ? item.cost : undefined;
}

/**
 * Normalizes a reactive model / plain-data item into the engine's
 * `ExpandableItem`, resolving the heavy referenced data the engine needs:
 * the flight option, the hotel id/name, and the place event type. The place
 * event type prefers the server-denormalized `place.eventType` and falls back to
 * the shared ruleset for places written before that migration.
 */
function toExpandableItem(item: ExpandableItineraryItem): ExpandableItem {
  const cost = getResolvedCost(item);
  const bookingStatus = item.booking?.status;
  const base: ExpandableItem = {
    id: item.id,
    type: item.type,
    title: item.title,
    startDateTime: item.startDateTime,
    endDateTime: item.endDateTime ?? undefined,
    ...(cost ? { cost } : {}),
    ...(bookingStatus ? { bookingStatus } : {}),
  };

  switch (item.type) {
    case 'flight': {
      const flightOption =
        'flightOption' in item && item.flightOption
          ? (item.flightOption as unknown as FlightOption)
          : item.referenceData && typeof item.referenceData === 'object'
            ? (item.referenceData as FlightOption)
            : undefined;
      return { ...base, flightOption };
    }
    case 'room':
      return {
        ...base,
        hotelId: getHotelIdForRoomItem(item),
        hotelName: getHotelNameForRoomItem(item),
        ...getHotelDestinationFields(item),
      };
    case 'place': {
      const place = 'place' in item ? (item as ItineraryItem).place : undefined;
      const placeEventType = (place?.eventType ??
        (place?.types?.length ? resolvePlaceEventType(place.types, place.primaryType) : undefined)) as
        | ItineraryItemEventType
        | undefined;
      // The place's own type, so the event can be named "Irish pub" rather than "Nightlife".
      const placePrimaryType = place?.primaryType ?? place?.types?.[0] ?? undefined;
      return { ...base, placeEventType, ...(placePrimaryType ? { placePrimaryType } : {}) };
    }
    default:
      return base;
  }
}

/**
 * Expands a single itinerary item into display events. Thin adapter over the
 * shared engine; preserved for per-item callers (e.g. `ItineraryItem.expandToEvents`).
 */
export function expandSingleItineraryItem(item: ExpandableItineraryItem): ItineraryItemEvent[] {
  try {
    return expandItineraryItem(toExpandableItem(item));
  } catch (error) {
    console.error('❌ Error expanding itinerary item:', error instanceof Error ? error.message : String(error), {
      itemId: item.id,
      itemType: item.type,
    });
    return [];
  }
}

/**
 * Groups room itinerary items by hotel and creates one hotel_checkin and one
 * hotel_checkout event per hotel, positioned relative to flights. Adapter over
 * the shared engine.
 */
export function createGroupedHotelEvents(
  roomItems: ExpandableItineraryItem[],
  flightEvents?: ItineraryItemEvent[],
): ItineraryItemEvent[] {
  return createGroupedHotelEventsEngine(roomItems.map(toExpandableItem), flightEvents);
}

/** Computes which hotel the user is staying at for each calendar day. Adapter over the shared engine. */
export function computeHotelStaysByDay(roomItems: ExpandableItineraryItem[]): Record<string, HotelStayInfo[]> {
  return computeHotelStaysByDayEngine(roomItems.map(toExpandableItem));
}

/**
 * Partitions a trip's ordered calendar days into destination groups (hotel/flight/
 * plan-anchored) with per-currency cost subtotals. Adapter over the shared engine —
 * normalizes reactive items (resolving hotel destination + cost) before grouping.
 */
export function computeDestinationGroups(
  items: ExpandableItineraryItem[],
  orderedDateKeys: string[],
  plan?: PlannedDestination[],
): DestinationGroup[] {
  return groupDaysByDestinationEngine(orderedDateKeys, items.map(toExpandableItem), plan);
}

/** Get icon name for event type */
export function getEventTypeIcon(type: ItineraryItemEvent['type'] | ItineraryItemData['type']): IconName {
  switch (type) {
    case 'flight': {
      return 'flight_takeoff';
    }
    case 'room': {
      return 'hotel';
    }
    case 'activity':
    case 'custom': {
      return 'event';
    }
    case 'flight_departure': {
      return 'flight_takeoff';
    }
    case 'layover': {
      return 'airline_stops';
    }
    case 'flight_arrival': {
      return 'flight_land';
    }
    case 'destination_arrival': {
      return 'flag';
    }
    case 'hotel_checkin': {
      return 'hotel';
    }
    case 'hotel_checkout': {
      return 'luggage';
    }
    default: {
      return getPlaceIcon(type);
    }
  }
}

/**
 * Parses HHMM format time string (e.g., "1500") to HH:mm format (e.g., "15:00")
 * @param hhmm - Time string in HHMM format (e.g., "1500", "1100")
 * @returns Time string in HH:mm format, or undefined if parsing fails or input is empty
 */
export function parseHHMMToTime(hhmm: string | undefined): string | undefined {
  if (!hhmm) return undefined;

  // Handle HHMM format (e.g., "1500" -> "15:00")
  if (/^\d{4}$/.test(hhmm)) {
    const hours = hhmm.slice(0, 2);
    const minutes = hhmm.slice(2, 4);
    return `${hours}:${minutes}`;
  }

  // Handle HH:MM format (already formatted)
  if (/^\d{1,2}:\d{2}$/.test(hhmm)) {
    return hhmm;
  }

  // Try to parse as time string
  try {
    const timeMatch = hhmm.match(/(\d{1,2}):?(\d{2})/);
    if (timeMatch) {
      const hours = timeMatch[1]!.padStart(2, '0');
      const minutes = timeMatch[2]!.padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  } catch {
    // If parsing fails, return undefined
  }

  return undefined;
}

/**
 * Combines a date (YYYY-MM-DD) with a time (HH:mm) to produce a local datetime string.
 * @returns Local datetime string in YYYY-MM-DDTHH:mm:ss (no timezone)
 */
export function combineDateAndTime(date: string, time: string): string {
  if (!date || !time) {
    throw new Error('Date and time are required');
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(`Invalid date format: ${date}. Expected YYYY-MM-DD`);
  }

  if (!/^\d{1,2}:\d{2}$/.test(time)) {
    throw new Error(`Invalid time format: ${time}. Expected HH:mm`);
  }

  const timeParts = time.split(':');
  const hours = Number(timeParts[0]);
  const minutes = Number(timeParts[1]);

  if (hours === undefined || minutes === undefined || Number.isNaN(hours) || Number.isNaN(minutes)) {
    throw new Error(`Invalid time format: ${time}. Expected HH:mm`);
  }

  // Build the string directly so the wall-clock date/time is preserved verbatim
  // (no timezone conversion), matching how itinerary times are stored.
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  return `${date}T${hh}:${mm}:00`;
}

/**
 * Adds a duration (in minutes) to a datetime string.
 * @returns New ISO datetime string with duration added
 */
export function addDurationToDateTime(dateTime: string, durationMinutes: number): string {
  if (!dateTime) {
    throw new Error('DateTime is required');
  }

  const date = parseISO(dateTime);
  if (!isValid(date)) {
    throw new Error(`Invalid datetime: ${dateTime}`);
  }

  return addMinutes(date, durationMinutes).toISOString();
}

/** Resolve itinerary items for an event (single item or grouped hotel items). */
export function resolveItemsForEvent(
  event: { itemId: string; type: string; metadata?: Record<string, unknown> },
  allItems: ItineraryItem[],
): ItineraryItem[] {
  const itemIds = event.metadata?.itemIds as string[] | undefined;
  if (
    (event.type === 'hotel_checkin' || event.type === 'hotel_checkout') &&
    Array.isArray(itemIds) &&
    itemIds.length > 0
  ) {
    return itemIds.map((id) => allItems.find((i) => i.id === id)).filter((i): i is ItineraryItem => i != null);
  }
  const item = allItems.find((i) => i.id === event.itemId);
  return item ? [item] : [];
}
